/**
 * NoAuthTokenError — typed pre-flight rejection (ABG-001 / Phase E1.1).
 *
 * Thrown by `createApiClient`'s request interceptor when an authenticated
 * request is attempted with no bearer token available. Replaces the legacy
 * "warn-and-fire" pattern that produced the 22-endpoint 401 cascade on
 * stale-token mount documented in the UAT log
 * `CRITICAL-FIXES/parcelpuffin-uat/dtownretail.dev.parcelpuffin.com-1777343878620.log`.
 *
 * Standards reference: `GUIDES/shared/standards/auth-bootstrap.md` (ABG-001).
 *
 * @module @puffin-core/api-client/errors/NoAuthTokenError
 */

/**
 * Typed pre-flight rejection for authenticated requests with no bearer token.
 *
 * Properties:
 * - `name`     — always `'NoAuthTokenError'`
 * - `code`     — always `'NO_AUTH_TOKEN'`
 * - `endpoint` — request URL path (post-baseURL normalization)
 * - `method`   — HTTP method, lowercased
 * - `requestId`— optional X-Request-ID for tracing (undefined when absent)
 * - `message`  — human-readable: `'No auth token available for ${method} ${endpoint}'`
 * - `cause`    — optional ES2022 chained-error reference
 * - `status`   — explicitly `undefined` (this is NOT a server 401)
 *
 * @example
 * throw new NoAuthTokenError({
 *   endpoint: '/api/v1/dashboard',
 *   method: 'GET',
 *   requestId: 'req_abc',
 * });
 */
export class NoAuthTokenError extends Error {
  /**
   * @param {Object} opts
   * @param {string} opts.endpoint - Request URL path (required, non-empty)
   * @param {string} opts.method - HTTP method (required, non-empty)
   * @param {string} [opts.requestId] - X-Request-ID for tracing
   * @param {unknown} [opts.cause] - Chained-error reference (ES2022)
   */
  constructor(opts) {
    if (!opts || typeof opts !== 'object') {
      throw new TypeError('NoAuthTokenError: constructor opts are required');
    }
    const { endpoint, method, requestId, cause } = opts;
    if (typeof endpoint !== 'string' || endpoint.length === 0) {
      throw new TypeError('NoAuthTokenError: opts.endpoint is required (non-empty string)');
    }
    if (typeof method !== 'string' || method.length === 0) {
      throw new TypeError('NoAuthTokenError: opts.method is required (non-empty string)');
    }

    const normalizedMethod = method.toLowerCase();
    const message = `No auth token available for ${normalizedMethod} ${endpoint}`;

    // ES2022 chained-error semantics: pass cause to super if supported.
    const errOpts = cause === undefined ? undefined : { cause };
    super(message, errOpts);

    this.name = 'NoAuthTokenError';
    this.code = 'NO_AUTH_TOKEN';
    this.endpoint = endpoint;
    this.method = normalizedMethod;
    if (requestId !== undefined) {
      this.requestId = requestId;
    }
    // status is explicitly undefined — distinguishes from server 401.
    this.status = undefined;

    // V8 captureStackTrace hides the constructor frame for cleaner traces.
    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(this, this.constructor);
    }
  }
}

// Anchor `name` on the prototype so `NoAuthTokenError.prototype.name` returns
// the canonical string (used by contract tests + downstream `err.name` checks
// even when the instance hasn't been constructed yet).
NoAuthTokenError.prototype.name = 'NoAuthTokenError';
